let _unlocked = false;
const _callbacks = new Set<() => void>();
let _listening = false;

// The full gesture set so the FIRST real interaction — mouse, touch, or keyboard
// — counts, on every platform.
const GESTURES = ['pointerdown', 'touchstart', 'click', 'keydown'] as const;

function onGesture(): void {
  _unlocked = true;
  // Copy before iterating — a callback may unsubscribe itself.
  for (const cb of [..._callbacks]) {
    try { cb(); } catch { /* ignore */ }
  }
}

function ensureListening(): void {
  if (_listening) return;
  if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') return;
  _listening = true;
  // CAPTURE phase: a bubble-phase document listener can be swallowed by a React
  // onClick that calls stopPropagation(), so the very first tap never unlocked /
  // retried playback and music only began after some later surface re-asserted it
  // (tester B2: "music doesn't start until I go to Settings and back"). Capture
  // fires before any app handler can stop it. NOT { once } — each gesture re-fires
  // the (idempotent) retry until playback actually lands. { passive } — we never
  // preventDefault here.
  const opts = { capture: true, passive: true } as AddEventListenerOptions;
  for (const ev of GESTURES) document.addEventListener(ev, onGesture, opts);
}

/**
 * iOS Safari and some Android WebViews block audio playback until a user
 * gesture. Call once at startup to arm the first-gesture unlock.
 *
 * Pass `onUnlock` to ALSO retry a blocked play() the instant the user first
 * interacts — the music provider passes () => player.ensurePlaying(), so a
 * mount-time autoplay block can't leave the bed silent until some later screen
 * happens to re-assert it. Idempotent. Returns an unsubscribe for the callback
 * (the shared gesture listeners stay armed for the session).
 */
export function initAudioUnlock(onUnlock?: () => void): () => void {
  ensureListening();
  if (!onUnlock) return () => {};
  _callbacks.add(onUnlock);
  return () => { _callbacks.delete(onUnlock); };
}

export function isAudioUnlocked(): boolean {
  return _unlocked;
}
